import { supabase } from '../../shared/supabase/supabaseClient';
import { registerSessionCacheClearHandler } from '../../shared/ports/sessionResetPort';
import type { ArcaEnvironment, BranchFiscalSettings, BranchFiscalSummary, FiscalContext, FiscalProfile, FiscalSetupStatus } from '../domain/fiscalTypes';

const FISCAL_CONTEXT_CACHE_MS = 5 * 60 * 1000;
const fiscalContextCache = new Map<string, { ctx: FiscalContext; fetchedAt: number }>();
const fiscalContextInFlight = new Map<string, Promise<FiscalContext>>();

const arcaStatusCache = new Map<string, { status: ArcaIntegrationStatus; fetchedAt: number }>();
const arcaStatusInFlight = new Map<string, Promise<ArcaIntegrationStatus>>();

export type ArcaIntegrationStatus = {
  setupStatus: FiscalSetupStatus | null;
  environment: ArcaEnvironment | null;
  /** null si la RPC vieja no trae el punto de venta de esta sucursal. */
  pointOfSale: number | null;
  /** null = desconocido. false = este local no está verificado en ARCA. */
  pvValidated: boolean | null;
};

function fiscalContextCacheKey(branchId: string, includeAllBranches: boolean): string {
  return `${branchId}:${includeAllBranches ? 'all' : 'one'}`;
}

type FiscalProfileRow = {
  organization_id: string;
  tax_regime: 'monotributo' | 'responsable_inscripto';
  emitter_cuit: string | null;
  business_name: string | null;
  domicilio_comercial: string | null;
  iibb: string | null;
  inicio_actividades: string | null;
  monotributo_category: string | null;
  environment: 'homologacion' | 'produccion';
  setup_status: 'draft' | 'validated' | 'error';
  setup_validated_at: string | null;
  setup_error: string | null;
  // Flujo CSR (#1510). `?? null` en el map: EFs viejas pueden no devolverlas.
  arca_csr_cuit?: string | null;
  arca_csr_environment?: 'homologacion' | 'produccion' | null;
  arca_csr_generated_at?: string | null;
  arca_key_source?: 'generated' | 'manual' | null;
  arca_cert_expires_at?: string | null;
};

type BranchFiscalRow = {
  branch_id: string;
  organization_id: string;
  point_of_sale: number | null;
  pv_validated_at: string | null;
  pv_last_invoice_number: number | null;
  pv_setup_error: string | null;
};

type BranchFiscalListRow = {
  branch_id: string;
  branch_name: string;
  point_of_sale: number | null;
  pv_validated_at: string | null;
  pv_last_invoice_number: number | null;
  pv_setup_error: string | null;
};

function mapFiscalProfile(row: FiscalProfileRow): FiscalProfile {
  return {
    organizationId: row.organization_id,
    taxRegime: row.tax_regime,
    emitterCuit: row.emitter_cuit,
    businessName: row.business_name,
    // `?? null`: EFs viejas (pre-migración) pueden no devolver estas keys.
    domicilioComercial: row.domicilio_comercial ?? null,
    iibb: row.iibb ?? null,
    inicioActividades: row.inicio_actividades ?? null,
    monotributoCategory: row.monotributo_category,
    environment: row.environment,
    setupStatus: row.setup_status,
    setupValidatedAt: row.setup_validated_at,
    setupError: row.setup_error,
    arcaCsrCuit: row.arca_csr_cuit ?? null,
    arcaCsrEnvironment: row.arca_csr_environment ?? null,
    arcaCsrGeneratedAt: row.arca_csr_generated_at ?? null,
    arcaKeySource: row.arca_key_source ?? null,
    arcaCertExpiresAt: row.arca_cert_expires_at ?? null,
  };
}

function mapBranchFiscal(row: BranchFiscalRow): BranchFiscalSettings {
  return {
    branchId: row.branch_id,
    organizationId: row.organization_id,
    pointOfSale: row.point_of_sale,
    pvValidatedAt: row.pv_validated_at,
    pvLastInvoiceNumber: row.pv_last_invoice_number,
    pvSetupError: row.pv_setup_error,
  };
}

function mapBranchFiscalSummary(row: BranchFiscalListRow, organizationId: string): BranchFiscalSummary {
  return {
    branchId: row.branch_id,
    branchName: row.branch_name,
    organizationId,
    pointOfSale: row.point_of_sale,
    pvValidatedAt: row.pv_validated_at,
    pvLastInvoiceNumber: row.pv_last_invoice_number,
    pvSetupError: row.pv_setup_error,
  };
}

export async function getFiscalContext(
  branchId: string,
  options?: { includeAllBranches?: boolean; force?: boolean }
): Promise<FiscalContext> {
  const cacheKey = fiscalContextCacheKey(branchId, options?.includeAllBranches === true);
  if (!options?.force) {
    const cached = fiscalContextCache.get(cacheKey);
    if (cached && Date.now() - cached.fetchedAt < FISCAL_CONTEXT_CACHE_MS) {
      return cached.ctx;
    }
  }

  // Dedupe in-flight: varios mounts (checkout panel, hub, readiness) sobre cache
  // fría disparaban invokes paralelos de la misma EF.
  const existing = fiscalContextInFlight.get(cacheKey);
  if (existing) return existing;
  const promise = fetchFiscalContextRemote(branchId, options?.includeAllBranches === true, cacheKey)
    .finally(() => {
      fiscalContextInFlight.delete(cacheKey);
    });
  fiscalContextInFlight.set(cacheKey, promise);
  return promise;
}

async function fetchFiscalContextRemote(
  branchId: string,
  includeAllBranches: boolean,
  cacheKey: string,
): Promise<FiscalContext> {
  const { data, error } = await supabase.functions.invoke('fiscal-profile-get', {
    body: {
      branch_id: branchId,
      include_all_branches: includeAllBranches,
    },
  });

  if (error) {
    console.warn('[fiscal] fiscal-profile-get', error.message);
    return { profile: null, branchFiscal: null };
  }

  const body = data as {
    profile?: FiscalProfileRow | null;
    branch_fiscal?: BranchFiscalRow | null;
    branches_fiscal?: BranchFiscalListRow[];
    organization_id?: string;
    error?: string;
  };

  if (body?.error) {
    console.warn('[fiscal] fiscal-profile-get', body.error);
    return { profile: null, branchFiscal: null };
  }

  const organizationId =
    body.organization_id ?? body.profile?.organization_id ?? body.branch_fiscal?.organization_id ?? '';
  const ctx: FiscalContext = {
    profile: body.profile ? mapFiscalProfile(body.profile) : null,
    branchFiscal: body.branch_fiscal ? mapBranchFiscal(body.branch_fiscal) : null,
    branchesFiscal: body.branches_fiscal?.map((row) => mapBranchFiscalSummary(row, organizationId)),
  };
  fiscalContextCache.set(cacheKey, { ctx, fetchedAt: Date.now() });
  return ctx;
}

export async function getFiscalProfile(branchId: string): Promise<FiscalProfile | null> {
  const ctx = await getFiscalContext(branchId);
  return ctx.profile;
}

/**
 * Status liviano del perfil fiscal vía RPC PostgREST (una sola llamada, ~ms).
 * Para consumidores que solo necesitan setup_status/environment (hub de
 * Integraciones). El contexto completo sigue viniendo de `getFiscalContext`
 * (Edge Function con permisos + branch_fiscal + lista de sucursales).
 */
export async function getArcaIntegrationStatus(
  branchId: string,
  options?: { force?: boolean },
): Promise<ArcaIntegrationStatus> {
  if (!options?.force) {
    const cached = arcaStatusCache.get(branchId);
    if (cached && Date.now() - cached.fetchedAt < FISCAL_CONTEXT_CACHE_MS) {
      return cached.status;
    }
  }

  const existing = arcaStatusInFlight.get(branchId);
  if (existing) return existing;
  const promise = fetchArcaIntegrationStatusRemote(branchId)
    .finally(() => {
      arcaStatusInFlight.delete(branchId);
    });
  arcaStatusInFlight.set(branchId, promise);
  return promise;
}

async function fetchArcaIntegrationStatusRemote(branchId: string): Promise<ArcaIntegrationStatus> {
  const { data, error } = await supabase.rpc('arca_integration_status_get', {
    p_branch_id: branchId,
  });
  if (error) {
    console.warn('[fiscal] arca_integration_status_get', error.message);
    return { setupStatus: null, environment: null, pointOfSale: null, pvValidated: null };
  }
  const row = (data ?? {}) as {
    setup_status?: FiscalSetupStatus | null;
    environment?: ArcaEnvironment | null;
    point_of_sale?: number | null;
    pv_validated?: boolean | null;
  };
  const hasBranchPv = Object.prototype.hasOwnProperty.call(row, 'pv_validated');
  const status: ArcaIntegrationStatus = {
    setupStatus: row.setup_status ?? null,
    environment: row.environment ?? null,
    pointOfSale: hasBranchPv && row.point_of_sale != null ? Number(row.point_of_sale) : null,
    pvValidated: hasBranchPv ? row.pv_validated === true : null,
  };
  arcaStatusCache.set(branchId, { status, fetchedAt: Date.now() });
  return status;
}

/**
 * Invalida el cache del contexto fiscal para una branch (o todas si no se pasa branchId).
 * Llamar después de cualquier escritura que cambie el profile/branchFiscal
 * (saveFiscalProfileRemote, validateBranchPointOfSale, validateArcaSetup, cutover a producción).
 */
export function invalidateFiscalContextCache(branchId?: string): void {
  if (branchId) {
    // Invalida ambas variantes (includeAllBranches true y false) para esa branch.
    for (const key of fiscalContextCache.keys()) {
      if (key.startsWith(`${branchId}:`)) {
        fiscalContextCache.delete(key);
      }
    }
    arcaStatusCache.delete(branchId);
  } else {
    fiscalContextCache.clear();
    arcaStatusCache.clear();
  }
}

registerSessionCacheClearHandler(() => {
  fiscalContextCache.clear();
  fiscalContextInFlight.clear();
  arcaStatusCache.clear();
  arcaStatusInFlight.clear();
});
